import { Box, Button, Card, CardBody, CardFooter, CardHeader, Flex, HStack, Heading, IconButton, Link, Menu, MenuButton, MenuItem, MenuList, Spacer, Text, VStack, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import { NavBar } from "../components/NavBar";
import { Footer } from "../components/Footer";
import React, { useEffect, useRef, useState } from "react";
import { Link as ReactLink, useNavigate } from 'react-router-dom';
import { ViewData } from "../data/ViewData";
import { EvmRoutes, RoutesData } from "../data/RoutesData";
import { ChainsInfo } from "../data/ChainsInfo";
import { FaSave } from "react-icons/fa";
import { IOUtils } from "../utils/IOUtils";
import { EvmWorker } from "../client/EvmWorker";
import { MessagePrefix, ethers } from "ethers";
import { delay } from "../utils/threads";
import { ZoraIcon } from "../icons/Icons";
import { Zora } from "../visualizer/Zora";
import { ChainNetworkBadge } from "../components/ChainNetworkBadge";
import { ContractTooltipCard } from "../components/ContractTooltipCard";

export const ZoraPage = () => {
    const isTestnet = ChainsInfo.zora.mainnet.contract.length === 0;
    const networkInfo = isTestnet ? ChainsInfo.zora.testnet : ChainsInfo.zora.mainnet;
    const abi = ChainsInfo.zora.abi;
    const [hex40, setHex40] = useState(ViewData.sampleHex);
    const svgDiv = useRef(null);
    const svgViewer = useRef(null);

    const [svgRendered, setSvgRendered] = useState(false);
    const [minting, setMinting] = useState(false);
    const [minted, setMinted] = useState(ViewData.MintStatus.zora);
    const [tokenId, setTokenId] = useState("");

    const [isDeployer, setIsDeployer] = useState(ViewData.ethAddress.toLocaleLowerCase() === networkInfo.deployer.toLocaleLowerCase());
    const [balance, setBalance] = useState("0");
    const [withdrawing, setWithdrawing] = useState(false);

    const mintPrice = "0.001";//"0.001000000042069"; // ETH
    const toast = useToast();
    const chainIdName = EvmRoutes.zora.title;
    const chainId = networkInfo.chainId;
    const etherscanPrefix = isTestnet ? "testnet." : "";
    const openseaPrefix = isTestnet ? "testnet." : "";

    const contractAddress: string = networkInfo.contract;
    const visualizer: Zora = new Zora();
    let svgElement: SVGSVGElement;
    let worker: EvmWorker;
    let contract: ethers.Contract;
    let tryLoaded = false;

    const renderSvg = () => {
        if (svgRendered || !svgDiv || !(svgDiv.current)) return;
        svgElement = visualizer.generate(hex40);
        (svgDiv.current as any).appendChild(svgElement);
        setSvgRendered(true);
    }

    const saveSvg = (id: string, collection: string) => {
        const div = document.getElementById(id);
        if (!div) return;
        const svgContent = div.innerHTML;
        const svgString = `<?xml version="1.0" encoding="UTF-8" standalone="no"?> <!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd"> ${svgContent}`;
        const dataUrl = 'data:image/svg+xml;charset=utf8,' + encodeURIComponent(svgString);
        IOUtils.saveDataURL(dataUrl, "image/svg+xml", `${hex40}.${collection}.svg`);
    }

    const ensureNetwork = async () => {
        if (!worker) await init();
        else await worker.init(chainId, ViewData.ethAddress);
        if (worker.chainId !== chainId) {
            toast({
                title: 'Error',
                description: "Please switch the network to **Zora**!",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }
        return true;
    }

    const mint = async () => {
        if (ViewData.MintStatus.zora) {
            toast({
                title: 'Error',
                description: "You have already minted!",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        // check network
        const checkNetwork = await ensureNetwork();
        if (!checkNetwork) return;

        const ctx = await worker.getContract(contractAddress, abi);
        contract = ctx.contract;
        // const count = await contract.count();
        // console.log(`${count} items minted.`);
        // check
        const checkMinted = await contract.minted();
        console.log(`Did ${ViewData.ethAddress} minted? ${checkMinted}`);
        ViewData.MintStatus.zora = minted;
        if (checkMinted) {
            toast({
                title: 'Error',
                description: "You have already minted!",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        // set event
        contract.on('Minted', (user: any, tokenId: number) => {
            ViewData.MintStatus.zora = true;
            toast({
                title: 'Congratulations!',
                description: "You have already minted your SBT!",
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        });
        // mint
        setMinting(true);
        try {
            await worker.mint(contract, mintPrice, toast);
            await doLoadToken();
            await loadBalance();
            setMinted(true);
        }
        finally {
            setMinting(false);
        }
    }

    const withdraw = async () => {
        if (balance === "0") {
            toast({
                title: '-_-|||',
                description: "There is no ETH in the contract.",
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        // check network
        const checkNetwork = await ensureNetwork();
        if (!checkNetwork) return;
        if (!contract) {
            toast({
                title: 'Error',
                description: "Failed to load the contract.",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        try {
            setWithdrawing(true);
            const r = await worker.withdrawAll(contract, toast);
            if (r) setBalance("0");
        }
        finally {
            setWithdrawing(false);
        }
    }

    const doLoadToken = async () => {
        const svg = await worker.loadTokenImage(contract, ViewData.ethAddress);
        setMinted(true);
        console.log(`svg: ${svg}`);
        while (!svgViewer.current) {
            await delay(500);
        }
        (svgViewer.current as any).innerHTML = svg;
    }

    const loadToken = async () => {
        // check network
        const checkNetwork = await ensureNetwork();
        if (!checkNetwork) return;

        const ctx = await worker.getContract(contractAddress, abi);
        contract = ctx.contract;
        // check minted
        const alreadyMinted = await contract.minted();
        tryLoaded = true;

        if (!alreadyMinted)
            return false;
        ViewData.MintStatus.zora = true;
        await doLoadToken();
    }

    const loadBalance = async () => {
        if (isDeployer) {
            const eth = await worker.provider.getBalance(contractAddress);
            const bal = ethers.formatEther(eth);
            setBalance(bal);
        }
    }

    const init = async () => {
        await EvmWorker.requestNetwork(networkInfo.requestParams);
        if (!worker) {
            worker = new EvmWorker();
            await worker.init(chainId, ViewData.ethAddress);
            setTokenId(`${worker.addrInt}`);
        }
        if (worker) {
            await loadToken();
            await loadBalance();
        }
    }

    useEffect(() => {
        // React advises to declare the async function directly inside useEffect
        // async function loadData() {
        // };
        // You need to restrict it at some point
        // This is just dummy code and should be replaced by actual
        init();
    }, []);

    return (
        <VStack spacing={4}>
            <NavBar />
            <Wrap>
                <WrapItem>
                    {minted ? <Card width="340px" m={5}>
                        <CardHeader>
                            <Heading size='md'><HStack><ZoraIcon /> <Text>Zora</Text><ChainNetworkBadge testnet={isTestnet} /></HStack></Heading>
                        </CardHeader>
                        <CardBody>
                            <div id="svgViewer" ref={svgViewer} />
                        </CardBody>
                        <CardFooter>
                            {/* <Button flex='1' variant='ghost' as={Link} target="_blank" href={`https://${openseaPrefix}opensea.io/account`}>OpenSea</Button> */}
                            <Button flex='1' variant='ghost' as={Link} target="_blank" href={`https://${etherscanPrefix}explorer.zora.energy/token/${contractAddress}/instance/${tokenId}`}>Etherscan</Button>
                        </CardFooter>
                    </Card> : <Card width="340px" m={5}>
                        <CardHeader>
                            <Heading size='md'><HStack><ZoraIcon /> <Text>Zora</Text><ChainNetworkBadge testnet={isTestnet} /></HStack></Heading>
                        </CardHeader>
                        <CardBody>
                            <div id="svgDiv" ref={svgDiv} />
                        </CardBody>
                        <CardFooter>
                            <Button flex='1' variant='ghost' isDisabled={svgRendered} onClick={renderSvg}>1. Generate</Button>
                            {/* <Menu>
                        <MenuButton as={IconButton} aria-label="Save" title="Save" variant="outline"
                            icon={<FaSave />} isDisabled={!svgRendered} />
                        <MenuList>
                            <MenuItem onClick={e => { saveSvg("svgDiv", `${chainIdName}`); }}>SVG</MenuItem>
                        </MenuList>
                    </Menu> */}
                            <Button flex='1' variant='ghost' isDisabled={!svgRendered || minted}
                                isLoading={minting} onClick={mint}>2. Mint (Price: {mintPrice} ETH)</Button>
                        </CardFooter>
                    </Card>}
                </WrapItem>
                <WrapItem>
                    <ContractTooltipCard isTestnet={isTestnet} contract={contractAddress} contractUri={`https://${etherscanPrefix}explorer.zora.energy/address/${contractAddress}`} />
                </WrapItem>
                {isDeployer ? <WrapItem>
                    <Card width="340px" m={5}>
                        <CardHeader>
                            <Heading size='md'>Balance in Contract</Heading>
                        </CardHeader>
                        <CardBody>
                            <Heading>{balance}</Heading>
                        </CardBody>
                        <CardFooter>
                            <Button flex='1' variant='ghost' isLoading={withdrawing} onClick={withdraw}>Withdraw</Button>
                        </CardFooter>
                    </Card>
                </WrapItem> : null}
            </Wrap>


            <Flex>
                <Link as={ReactLink} to={RoutesData.Start}>Mint</Link> &nbsp;on more chains!
            </Flex>
            <Footer />
        </VStack>
    );
}